import { Fragment, useEffect, useState } from "react";
import { Modal } from "@/components/Modal";
import useModal from "@/hooks/useModal";
import { Spacer } from "@/components/Spacer";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLanguage } from "@/hooks/useLanguage";

export default function ContactUsModal({
  isOpenModal,
  closeModal,
}: {
  isOpenModal: boolean;
  closeModal: () => void;
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [content, setContent] = useState("");
  const [privacyCheck, setPrivacyCheck] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const { changeLanguage, isKorean } = useLanguage();

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  const isFormValid = () => {
    return (
      name.trim() !== "" &&
      company.trim() !== "" &&
      phone.trim() !== "" &&
      email.trim() !== "" &&
      content.trim() !== "" &&
      privacyCheck
    );
  };

  const submitForm = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        name: name,
        company: company,
        contact: phone,
        email: email,
        message: content,
        isAgreePrivacyPolicy: privacyCheck,
      };

      const response = await fetch(
        "https://nimda.healthier.kr/admin/mediology/landing/inquiry",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        }
      );

      if (response.ok) {
        alert(
          isKorean
            ? "문의 사항이 정상적으로 접수되었습니다."
            : "Your inquiry has been successfully submitted."
        );
        // 폼 초기화
        setName("");
        setCompany("");
        setPhone("");
        setEmail("");
        setContent("");
        setPrivacyCheck(false);
        closeModal();
      } else {
        throw new Error("Error");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPrivacyPolicyText = () => {
    return isKorean ? (
      <span className="flex flex-row items-start gap-[4px] flex-wrap">
        <span
          className="text-accentLinkBlue cursor-pointer underline whitespace-nowrap"
          onClick={() => setShowPrivacyPolicy(true)}
        >
          개인정보 수집 및 이용약관
        </span>
        을 확인하였으며, 이에 동의합니다.
        <img
          src="assets/images/require.png"
          alt="require"
          className="w-[7.81px] h-[7.95px]"
        />
      </span>
    ) : (
      <span>
        {" "}
        I have read and agree to the{" "}
        <span
          className="text-accentLinkBlue cursor-pointer underline"
          onClick={() => setShowPrivacyPolicy(true)}
        >
          Privacy Policy and Terms of Use.
        </span>
      </span>
    );
  };

  return (
    <Modal isOpen={isOpenModal} onClose={closeModal} isMobile={isMobile}>
      <div className="flex flex-col items-start h-full ">
        {/* 헤더 부분 - 고정 */}
        <div className="w-full desktop:px-[40px] desktop:pt-[60px] px-[20px] pt-[50px]">
          <div
            className={`w-full flex flex-row items-start justify-between ${
              isKorean ? "font-pre-bold" : "font-eng-bold"
            } desktop:text-[28px] text-[20px] text-[#17191C]`}
            style={{ letterSpacing: isKorean ? "0" : "-0.3px" }}
          >
            {!showPrivacyPolicy
              ? isKorean
                ? "문의 사항을 남겨주세요"
                : "Get in Touch"
              : isKorean
              ? "개인정보 수집 및 이용 동의서"
              : "Privacy Policy and Terms of Use"}
            <div
              className="flex flex-row items-center gap-[4px] cursor-pointer"
              onClick={() => {
                if (showPrivacyPolicy) {
                  setShowPrivacyPolicy(false);
                } else {
                  closeModal();
                }
              }}
            >
              <span
                className={`font-eng-medium desktop:text-[24px] text-[16px] text-[rgba(63, 68, 79, 0.68)] ${
                  isKorean ? "font-pre-medium" : "font-eng-medium"
                }`}
              >
                {isKorean ? "닫기" : "Close"}
              </span>
              <img
                src="assets/images/close.png"
                className="desktop:w-[28px] desktop:h-[28px] w-[16px] h-[16px]"
              />
            </div>
          </div>
        </div>

        {!showPrivacyPolicy ? (
          <Fragment>
            <Spacer height={20} />
            <div
              className={`desktop:px-[40px] px-[20px] ${
                isKorean ? "font-pre-medium" : "font-eng-medium"
              } desktop:text-[20px] text-[13px]`}
              style={{ letterSpacing: "-0.3px" }}
            >
              {isKorean
                ? "아래에 문의 사항을 남겨주시면, 빠른 시일 내에 연락드리겠습니다."
                : "Leave your inquiry below and we'll get back to you shortly."}
            </div>

            {/* 스크롤 가능한 컨텐츠 부분 */}
            <div
              className="w-full flex-1 overflow-y-auto desktop:px-[40px] desktop:pb-[60px] px-[20px] pb-[50px]"
              style={{
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
              }}
            >
              <Spacer height={40} />

              <div
                className="font-eng-semibold desktop:text-[20px] text-[16px] text-[#17191C] leading-[150%] flex flex-row items-start gap-[4px]"
                style={{ letterSpacing: "-0.3px" }}
              >
                {isKorean ? "이름" : "Name"}
                <img
                  src="assets/images/require.png"
                  alt="require"
                  className="w-[7.81px] h-[7.95px]"
                />
              </div>
              <Spacer height={12} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full desktop:p-[24px] p-[16px] rounded-[8px] desktop:rounded-[16px] border border-solid border-[rgba(218,223,230,0.60)] bg-[#F5F6FA] font-eng-regular leading-[150%] text-[#17191C] placeholder:text-[rgba(63, 68, 79, 0.68)] desktop:text-[18px] text-[15px] ${
                  isKorean ? "font-pre-regular" : "font-eng-regular"
                }`}
                placeholder={
                  isKorean ? "이름을 입력해주세요" : "Please enter your name"
                }
                style={{ letterSpacing: "-0.3px" }}
              />
              <Spacer height={32} />

              <div
                className={`desktop:text-[20px] text-[16px] text-[#17191C] leading-[150%] flex flex-row items-start gap-[4px] ${
                  isKorean ? "font-pre-semibold" : "font-eng-semibold"
                }`}
                style={{ letterSpacing: "-0.3px" }}
              >
                {isKorean ? "상호명" : "Company / Organization name"}
                <img
                  src="assets/images/require.png"
                  alt="require"
                  className="w-[7.81px] h-[7.95px]"
                />
              </div>
              <Spacer height={12} />
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={`w-full desktop:p-[24px] p-[16px] rounded-[8px] desktop:rounded-[16px] border border-solid border-[rgba(218,223,230,0.60)] bg-[#F5F6FA] font-pre-regular leading-[150%] text-[#17191C] placeholder:text-[rgba(63, 68, 79, 0.68)] desktop:text-[18px] text-[15px] ${
                  isKorean ? "font-pre-regular" : "font-eng-regular"
                }`}
                placeholder={
                  isKorean
                    ? "상호명을 입력해주세요"
                    : "Please enter your company / organization"
                }
                style={{ letterSpacing: "-0.3px" }}
              />

              <Spacer height={32} />

              <div
                className={`desktop:text-[20px] text-[16px] text-[#17191C] leading-[150%] flex flex-row items-start gap-[4px] ${
                  isKorean ? "font-pre-semibold" : "font-eng-semibold"
                }`}
                style={{ letterSpacing: "-0.3px" }}
              >
                {isKorean ? "연락처" : "Contact number"}
                <img
                  src="assets/images/require.png"
                  alt="require"
                  className="w-[7.81px] h-[7.95px]"
                />
              </div>
              <Spacer height={12} />
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`w-full desktop:p-[24px] p-[16px] rounded-[8px] desktop:rounded-[16px] border border-solid border-[rgba(218,223,230,0.60)] bg-[#F5F6FA] font-pre-regular leading-[150%] text-[#17191C] placeholder:text-[rgba(63, 68, 79, 0.68)] desktop:text-[18px] text-[15px] ${
                  isKorean ? "font-pre-regular" : "font-eng-regular"
                }`}
                placeholder={
                  isKorean
                    ? "연락처를 입력해주세요"
                    : "Please enter your contact number"
                }
                style={{ letterSpacing: "-0.3px" }}
              />

              <Spacer height={32} />

              <div
                className={`desktop:text-[20px] text-[16px] text-[#17191C] leading-[150%] flex flex-row items-start gap-[4px] ${
                  isKorean ? "font-pre-semibold" : "font-eng-semibold"
                }`}
                style={{ letterSpacing: "-0.3px" }}
              >
                {isKorean ? "이메일" : "Email address"}
                <img
                  src="assets/images/require.png"
                  alt="require"
                  className="w-[7.81px] h-[7.95px]"
                />
              </div>
              <Spacer height={12} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full desktop:p-[24px] p-[16px] rounded-[8px] desktop:rounded-[16px] border border-solid border-[rgba(218,223,230,0.60)] bg-[#F5F6FA] font-pre-regular leading-[150%] text-[#17191C] placeholder:text-[rgba(63, 68, 79, 0.68)] desktop:text-[18px] text-[15px] ${
                  isKorean ? "font-pre-regular" : "font-eng-regular"
                }`}
                placeholder={
                  isKorean
                    ? "이메일을 입력해주세요"
                    : "Please enter your email address"
                }
                style={{ letterSpacing: "-0.3px" }}
              />

              <Spacer height={32} />

              <div
                className={`desktop:text-[20px] text-[16px] text-[#17191C] leading-[150%] flex flex-row items-start gap-[4px] ${
                  isKorean ? "font-pre-semibold" : "font-eng-semibold"
                }`}
                style={{ letterSpacing: "-0.3px" }}
              >
                {isKorean ? "문의 내용" : "Inquiry Details"}
                <img
                  src="assets/images/require.png"
                  alt="require"
                  className="w-[7.81px] h-[7.95px]"
                />
              </div>
              <Spacer height={12} />
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={2}
                className={`w-full desktop:p-[24px] p-[16px] rounded-[8px] desktop:rounded-[16px] border border-solid border-[rgba(218,223,230,0.60)] bg-[#F5F6FA] font-pre-regular leading-[150%] text-[#17191C] placeholder:text-[rgba(63, 68, 79, 0.68)] resize-none desktop:text-[18px] text-[15px] ${
                  isKorean ? "font-pre-regular" : "font-eng-regular"
                }`}
                placeholder={
                  isKorean
                    ? "문의 내용을 입력해주세요"
                    : "Please enter your message"
                }
                style={{ letterSpacing: "-0.3px" }}
              />

              <Spacer height={32} />

              <div className="flex flex-row items-start desktop:items-center gap-[8px]">
                <img
                  src={
                    privacyCheck
                      ? "assets/images/checked-box.png"
                      : "assets/images/non-check-box.png"
                  }
                  alt="require"
                  className="w-[20px] h-[20px]"
                  onClick={() => setPrivacyCheck(!privacyCheck)}
                />
                <div
                  className={`font-eng-medium desktop:text-[18px] text-[14px] text-[#17191C] ${
                    isKorean ? "font-pre-medium" : "font-eng-medium"
                  }`}
                >
                  {renderPrivacyPolicyText()}
                </div>
              </div>
              <Spacer height={50} />

              <div
                onClick={submitForm}
                className={`w-max flex flex-row items-center gap-[6px] desktop:py-[16px] desktop:px-[24px] py-[10px] px-[12px] desktop:rounded-[8px] rounded-[8px] cursor-pointer ${
                  isFormValid() && !isSubmitting
                    ? "bg-[#5464F2]"
                    : "bg-[#CCCCCC] cursor-not-allowed"
                }`}
              >
                <span
                  className={`${
                    isKorean ? "font-pre-medium" : "font-eng-medium"
                  } desktop:text-[16px] text-[14px] leading-[125%]  ${
                    isFormValid() && !isSubmitting
                      ? "text-[#FFFFFF]"
                      : "text-[#888888]"
                  }`}
                  style={{ letterSpacing: "-0.3px" }}
                >
                  {isSubmitting
                    ? isKorean
                      ? "제출중..."
                      : "Submitting..."
                    : isKorean
                    ? "제출"
                    : "Submit"}
                </span>
                {!isSubmitting && (
                  <img
                    src="assets/images/arrow-right.png"
                    alt="arrow-right"
                    className={`desktop:w-[20px] desktop:h-[20px] w-[16px] h-[16px] ${
                      !isFormValid() ? "opacity-50" : ""
                    }`}
                  />
                )}
              </div>
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div
              className="w-full py-[24px] desktop:px-[40px] px-[20px] font-eng-regular leading-[150%] text-[#17191C] overflow-y-auto flex-1"
              style={{
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ node, ...props }) => (
                    <p
                      className={`text-[#343434] text-[14px] leading-[150%] ${
                        isKorean ? "font-pre-regular" : "font-eng-regular"
                      }`}
                      style={{ letterSpacing: "-0.5px" }}
                      {...props}
                    />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1
                      className={`text-[#001122] text-[24px] leading-[1.3em] mt-3 mb-2 ${
                        isKorean ? "font-pre-bold" : "font-eng-bold"
                      }`}
                      {...props}
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      className={`text-[#001122] text-[20px] leading-[1.3em] mt-3 mb-2 ${
                        isKorean ? "font-pre-bold" : "font-eng-bold"
                      }`}
                      {...props}
                    />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3
                      className={`text-[#001122] text-[18px] leading-[1.3em] mt-3 mb-2 ${
                        isKorean ? "font-pre-bold" : "font-eng-bold"
                      }`}
                      {...props}
                    />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc ml-5 my-2" {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol className="list-decimal ml-5 my-2" {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li
                      className={`text-[#343434] text-[14px] leading-[150%] my-1 ${
                        isKorean ? "font-pre-regular" : "font-eng-regular"
                      }`}
                      style={{ letterSpacing: "-0.5px" }}
                      {...props}
                    />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong
                      className={`text-[#343434] ${
                        isKorean ? "font-pre-bold" : "font-eng-bold"
                      }`}
                      {...props}
                    />
                  ),
                  a: ({ node, ...props }) => (
                    <a
                      className={`text-[#0066CC] underline ${
                        isKorean ? "font-pre-regular" : "font-eng-regular"
                      }`}
                      {...props}
                    />
                  ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      className="border-l-4 border-[#DDDDDD] pl-4 my-3 italic text-[#555555]"
                      {...props}
                    />
                  ),
                  table: ({ node, ...props }) => (
                    <div className="overflow-x-auto w-full my-3">
                      <table className="w-full border-collapse" {...props} />
                    </div>
                  ),
                  thead: ({ node, ...props }) => (
                    <thead className="bg-[#F0F0F0]" {...props} />
                  ),
                  tbody: ({ node, ...props }) => <tbody {...props} />,
                  tr: ({ node, ...props }) => (
                    <tr className="border-b border-[#DDDDDD]" {...props} />
                  ),
                  th: ({ node, ...props }) => (
                    <th
                      className={`text-[#343434] p-2 text-left ${
                        isKorean ? "font-pre-bold" : "font-eng-bold"
                      }`}
                      {...props}
                    />
                  ),
                  td: ({ node, ...props }) => (
                    <td
                      className={`text-[#343434] p-2 ${
                        isKorean ? "font-pre-regular" : "font-eng-regular"
                      }`}
                      {...props}
                    />
                  ),
                }}
              >
                {isKorean
                  ? `㈜메디올로지(이하 ‘회사’)는 「개인정보 보호법」 및 관련 법령에 따라, 도입 관련 문의 접수 및 응대를 위해 아래와 같이 귀하의 개인정보를 수집·이용하고자 합니다. 
                  
내용을 확인하신 후 동의 부탁드립니다.

1. 수집 항목
- 상호명
- 담당자명
- 연락처
- 이메일 주소
- 문의 내용
- 연락 가능 시간

2. 수집 및 이용 목적
- 헬시어 도입 및 제휴 관련 상담 및 대응
- 관련 서비스 안내 및 회신
- 이용자 식별 및 문의 응대 이력 관리


3. 보유 및 이용 기간
- 수집일로부터 1년간 보관 후 지체 없이 파기합니다. 단, 관련 법령에 따라 보존이 필요한 경우 해당 법령에 따릅니다.


4. 동의 거부 권리 및 불이익
- 귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있으며, 동의를 거부하실 경우 도입 상담 및 서비스 안내가 제한될 수 있습니다.`
                  : `
Consent to Collection and Use of Personal Information

Mediology Co., Ltd. (“the Company”) collects and uses your personal information in accordance with applicable U.S. data privacy laws for the purpose of responding to inquiries related to the adoption or partnership of the Healthier service. Please review the details below and provide your consent.

1. Information We Collect
- Company name
- Contact person’s name
- Phone number
- Email address
- Inquiry content

2. How We Use Your Information
- To respond to your inquiry about our services or partnership opportunities
- To send relevant information and follow-up communication
- To verify your identity and manage inquiry history

3. Data Retention

Your information will be retained for up to one (1) year from the date of collection and then securely deleted. We may retain data longer if required by applicable federal or state laws.

4. Your Rights and Choices

Providing your personal information is voluntary. However, if you choose not to consent, we may be unable to provide detailed consultation or respond to your inquiry.





`}
              </ReactMarkdown>
            </div>
          </Fragment>
        )}
      </div>
    </Modal>
  );
}
